import { Spinner, Button } from "@tellescope/design-system";
import { cn } from "@tellescope/utils";
import { useExternalRedirect } from "@tellescope/react-hooks";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useState } from "react";
import type { EnduserSession } from "@tellescope/sdk";
import { useTellescopeEnduserForm, type UseTellescopeEnduserFormArgs } from "./hooks/use-tellescope-enduser-form";
import { useFormAnalytics } from "./hooks/use-form-analytics";
import { EnduserFormWizardField } from "./EnduserFormWizardField";
import type { ContinueToFormInApp } from "./useResolveRedirectTarget";
import { EnduserFormWizardThanksScreen } from "./EnduserFormWizardThanksScreen";
import { EnduserFormWizardSubmittingScreen } from "./EnduserFormWizardSubmittingScreen";
import { toAbsoluteUrl } from "./utils";

export interface EnduserFormWizardProps {
  formArgs: UseTellescopeEnduserFormArgs;
  onComplete: () => void;
  session?: EnduserSession | null;
  organizationSubdomain?: string;
  orgLogoSrc?: string;
  documentGroupBackUrl?: string;
  continueToFormInApp?: ContinueToFormInApp;
}

export const EnduserFormWizard = ({
  formArgs,
  onComplete,
  session,
  organizationSubdomain,
  orgLogoSrc,
  documentGroupBackUrl,
  continueToFormInApp,
}: EnduserFormWizardProps) => {
  const { t } = useTranslation("forms");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const enduserForm = useTellescopeEnduserForm(formArgs);

  useFormAnalytics(enduserForm?.form?.gtmTag, enduserForm?.form?.ga4measurementId);

  const backgroundColor = enduserForm?.form?.backgroundColor;
  const wrapperStyle = backgroundColor?.toLowerCase() !== "#f4f3fa" ? { backgroundColor } : undefined;

  const configuredRedirectURL = enduserForm?.form?.submitRedirectURL;
  const submitRedirectURL = configuredRedirectURL ? toAbsoluteUrl(configuredRedirectURL) : undefined;

  const autoSubmitRedirectURL = enduserForm?.autoSubmitted ? submitRedirectURL : undefined;
  const resolveAutoSubmitRedirect = useCallback(
    async () => (autoSubmitRedirectURL ? { url: autoSubmitRedirectURL } : undefined),
    [autoSubmitRedirectURL]
  );
  // Stays undefined until the autosubmit lands, so the hook's fire-once latch is not spent on a
  // render that has no destination yet.
  useExternalRedirect(autoSubmitRedirectURL ? resolveAutoSubmitRedirect : undefined);

  if (isSubmitted) {
    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardThanksScreen
          htmlMessage={enduserForm?.form?.htmlThanksMessage}
          message={enduserForm?.form?.thanksMessage}
          onDone={onComplete}
        />
      </div>
    );
  }

  if (!enduserForm || !enduserForm.activeField) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full" style={wrapperStyle}>
        <Spinner size="sm">{t("Loading form...")}</Spinner>
      </div>
    );
  }

  const {
    activeField,
    getFieldProps,
    currentResponses,
    enduser,
    form,
    goToNextField,
    goToPreviousField,
    isNextDisabled,
    isPreviousDisabled,
    showSubmit,
    submitForm,
    isSubmitted: isResponseSubmitted,
    submitFormMutation,
    submitPhase,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    uploadProgress,
    progress,
    fieldsArr,
    logicOptions,
    applyDatabasePrepopulation,
    setCustomerId,
    fieldErrors,
  } = enduserForm;

  if (submitPhase !== "idle") {
    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardSubmittingScreen submitPhase={submitPhase} uploadProgress={uploadProgress} />
      </div>
    );
  }

  // An autosubmitted response is complete, so the wizard shows the same thanks screen the
  // Submit button leads to rather than returning to the answered field.
  if (autoSubmitted) {
    // A configured redirect takes precedence over the thanks screen: showing it would let the
    // patient click Done and close the dialog, only for the pending navigation to yank them
    // elsewhere. Hold on a spinner until the redirect lands. Gated on the normalized URL so a
    // target that cannot be navigated to falls through rather than holding the spinner forever.
    if (submitRedirectURL) {
      return (
        <div className="flex flex-col items-center justify-center p-8 h-full" style={wrapperStyle}>
          <Spinner size="sm">{t("Redirecting. Please wait...")}</Spinner>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full" style={wrapperStyle}>
        <EnduserFormWizardThanksScreen
          htmlMessage={form?.htmlThanksMessage}
          message={form?.thanksMessage}
          onDone={onComplete}
        />
      </div>
    );
  }

  const percentageComplete = progress.toFixed(0);

  return (
    <div className="flex flex-col h-full" style={wrapperStyle}>
      <div className="px-4 py-2 border-b flex-shrink-0">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t("{{percentageComplete}}% complete", {
              percentageComplete,
            })}
          </span>
        </div>
        <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${percentageComplete}%` }}
          />
        </div>
      </div>

      {orgLogoSrc && (
        <div className="px-4 pt-4 pb-2 flex justify-center flex-shrink-0">
          <img src={orgLogoSrc} alt="" className="h-10 object-contain" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-4 min-w-full">
          {!!session && (
            <EnduserFormWizardField
              node={activeField}
              session={session}
              enduserId={formArgs.enduserId}
              getFieldProps={getFieldProps}
              form={form}
              currentResponses={currentResponses}
              enduser={enduser}
              fieldsArr={fieldsArr}
              logicOptions={logicOptions}
              organizationSubdomain={organizationSubdomain}
              submitForm={submitForm}
              documentGroupBackUrl={documentGroupBackUrl}
              isResponseSubmitted={isResponseSubmitted}
              continueToFormInApp={continueToFormInApp}
              applyDatabasePrepopulation={applyDatabasePrepopulation}
              setCustomerId={setCustomerId}
              autoFocus
            />
          )}
          {(submitFormMutation.error || submitError || autoSubmitError) && (
            <p className="text-sm text-destructive">{t("Failed to submit form. Please try again.")}</p>
          )}
        </div>
      </div>

      <div className="px-4 py-3 border-t flex-shrink-0">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousField}
            disabled={isPreviousDisabled}
            className={cn(isPreviousDisabled && "invisible")}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("Back")}
          </Button>

          {showSubmit ? (
            <Button
              size="sm"
              onClick={async () => {
                setSubmitError(false);
                try {
                  await submitForm();
                } catch {
                  setSubmitError(true);
                  return;
                }
                // A configured target that does not normalize falls through to the thanks screen:
                // the response is already saved, so there is a sane place to land.
                if (submitRedirectURL) {
                  window.location.href = submitRedirectURL;
                  return;
                }
                setIsSubmitted(true);
              }}
              disabled={
                isNextDisabled ||
                submitFormMutation.isPending ||
                isAutoSubmitting ||
                Object.values(fieldErrors).some((e) => e !== null)
              }
            >
              {t("Submit")}
            </Button>
          ) : (
            <Button size="sm" onClick={goToNextField} disabled={isNextDisabled || isAutoSubmitting}>
              {t("Next")}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
